import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslations } from "next-intl";

type IconComponent =
    | React.ComponentType<{ className?: string }>
    | React.ComponentType<React.SVGProps<SVGSVGElement>>;

export interface FeatureItem {
    icon?: IconComponent;
    title: string;
    description: string;
}

interface ServicesSectionProps {
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    features?: FeatureItem[];
    speed?: number;                // px/second (автоскрол)
    cardMinWidth?: number;         // мінімальна ширина картки
    gapPx?: number;                // відступ між картками
    pauseOnInteract?: boolean;     // пауза при взаємодії
    ignoreReducedMotion?: boolean; // ігнорувати системний reduce-motion
}

export default function ServicesSection({
                                            title,
                                            subtitle,
                                            features = [],
                                            speed = 50,
                                            cardMinWidth = 300,
                                            gapPx = 32,
                                            pauseOnInteract = true,
                                            ignoreReducedMotion = false,
                                        }: ServicesSectionProps) {
    const t = useTranslations("servicesSection");

    const containerRef = useRef<HTMLDivElement | null>(null);
    const trackRef = useRef<HTMLDivElement | null>(null);

    const rafRef = useRef<number>(0);
    const lastTsRef = useRef<number>(0);
    const loopWidthRef = useRef<number>(0);
    const positionRef = useRef<number>(0);
    const dragRef = useRef({ active: false, startX: 0, startScrollLeft: 0 });

    const [paused, setPaused] = useState(false);

    // ===== контент (подвоюємо для безшовного циклу)
    const COPIES = 2;
    const rendered = useMemo(() => {
        if (!features || features.length === 0) return [];
        const base = features.length === 1 ? [...features, ...features] : features;
        const copies: FeatureItem[] = [];
        for (let i = 0; i < COPIES; i++) copies.push(...base);
        return copies;
    }, [features]);

    // ===== виміряти ширину одного "циклу"
    const measureLoopWidth = () => {
        const track = trackRef.current;
        if (!track) return;

        const itemsPerCycle = track.children.length / COPIES;
        if (!Number.isInteger(itemsPerCycle) || itemsPerCycle <= 0) return;

        const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || gapPx;
        let width = 0;
        for (let index = 0; index < itemsPerCycle; index += 1) {
            const item = track.children.item(index) as HTMLElement | null;
            if (item) width += item.getBoundingClientRect().width + gap;
        }

        loopWidthRef.current = width;
    };

    // ===== обгортання scrollLeft в межах довжини одного циклу
    const wrapScrollLeft = (x: number) => {
        const w = loopWidthRef.current;
        if (w <= 0) return x;
        let res = x % w;
        if (res < 0) res += w;
        return res;
    };

    const setScrollLeft = (el: HTMLDivElement, left: number, behavior: ScrollBehavior = "auto") => {
        positionRef.current = left;

        if (behavior === "auto") {
            el.scrollLeft = left;
            return;
        }

        if (typeof el.scrollTo === "function") {
            try {
                el.scrollTo({ left, behavior });
                return;
            } catch {}
        }

        el.scrollLeft = left;
    };

    // ===== ширина однієї картки (із gap)
    const getItemWidth = () => {
        const track = trackRef.current;
        if (!track) return cardMinWidth + gapPx;
        const first = track.children.item(0) as HTMLElement | null;
        const w = first?.getBoundingClientRect().width ?? cardMinWidth;
        return w + gapPx;
    };

    // ===== плавний перехід на сусідню картку
    const scrollToSiblingCard = (direction: 1 | -1) => {
        const el = containerRef.current;
        if (!el || loopWidthRef.current <= 0) return;

        const resumeLater = !paused;
        setPaused(true);

        const itemW = getItemWidth();
        const currentIndex = Math.round(positionRef.current / itemW);
        const nextIndex = currentIndex + direction;
        const target = wrapScrollLeft(nextIndex * itemW);

        setScrollLeft(el, target, "smooth");

        window.setTimeout(() => {
            if (resumeLater && pauseOnInteract) setPaused(false);
        }, 400);
    };

    // ===== автоскрол (із фіксами для iOS)
    const tick = (ts: number) => {
        const el = containerRef.current;
        if (!el) {
            rafRef.current = requestAnimationFrame(tick);
            return;
        }

        if (paused || dragRef.current.active) {
            lastTsRef.current = ts;
            rafRef.current = requestAnimationFrame(tick);
            return;
        }

        if (!ignoreReducedMotion && typeof window !== "undefined" && "matchMedia" in window) {
            const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            if (prefersReduced) {
                lastTsRef.current = ts;
                rafRef.current = requestAnimationFrame(tick);
                return;
            }
        }

        const last = lastTsRef.current || ts;
        const dt = (ts - last) / 1000;
        lastTsRef.current = ts;

        if (dt > 0 && loopWidthRef.current > 0) {
            const dx = speed * dt;
            const next = wrapScrollLeft(positionRef.current + dx);
            setScrollLeft(el, next, "auto");
        }

        rafRef.current = requestAnimationFrame(tick);
    };

    const start = () => {
        cancel();
        lastTsRef.current = 0;
        rafRef.current = requestAnimationFrame(tick);
    };

    const cancel = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
    };

    // ===== drag для ручного перетягування
    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        const el = containerRef.current;
        if (!el) return;
        if (pauseOnInteract) setPaused(true);
        dragRef.current.active = true;
        dragRef.current.startX = e.clientX;
        dragRef.current.startScrollLeft = el.scrollLeft;
        positionRef.current = el.scrollLeft;
        el.setPointerCapture?.((e as any).pointerId);
    };

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        const el = containerRef.current;
        if (!el || !dragRef.current.active) return;
        const dx = e.clientX - dragRef.current.startX;
        const next = wrapScrollLeft(dragRef.current.startScrollLeft - dx * 1.2);
        setScrollLeft(el, next, "auto");
    };

    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        const el = containerRef.current;
        dragRef.current.active = false;
        if (pauseOnInteract) setPaused(false);
        try {
            el?.releasePointerCapture?.((e as any).pointerId);
        } catch {}
    };

    // ===== життєвий цикл
    useEffect(() => {
        if (rendered.length === 0) return;

        measureLoopWidth();
        if (containerRef.current) {
            positionRef.current = wrapScrollLeft(containerRef.current.scrollLeft);
        }
        cancel();
        start();

        const onResize = () => {
            measureLoopWidth();
        };
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null;
        if (trackRef.current) ro?.observe(trackRef.current);
        if (containerRef.current) ro?.observe(containerRef.current);
        window.addEventListener("resize", onResize);

        return () => {
            cancel();
            ro?.disconnect();
            window.removeEventListener("resize", onResize);
        };
    }, [rendered.length, speed, cardMinWidth, gapPx]);

    if (rendered.length === 0) return null;

    return (
        <section className="section services py-24 bg-gray-50">
            <style>{`
        .services { min-height: 600px; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

            <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-16">
                    <div className="max-w-2xl mx-auto">
                        <h2 className="text-4xl font-bold mb-6">
                            {title ?? (
                                <>
                                    {t("header.titleBefore")} <span className="text-blue-600">{t("header.titleHighlight")}</span>{" "}
                                    {t("header.titleAfter")}
                                </>
                            )}
                        </h2>
                        <p className="text-gray-600">{subtitle ?? t("header.subtitle")}</p>
                    </div>
                </div>

                <div className="relative">
                    <button
                        className="md:hidden absolute left-0 top-0 bottom-0 w-20 z-10
              flex items-center justify-center
              bg-gradient-to-r from-white/80 to-transparent active:from-white"
                        onClick={() => scrollToSiblingCard(-1)}
                        aria-label={t("a11y.previous")}
                    >
                        <ChevronLeft className="w-12 h-12 text-gray-700" strokeWidth={3} />
                    </button>

                    <button
                        className="md:hidden absolute right-0 top-0 bottom-0 w-20 z-10
              flex items-center justify-center
              bg-gradient-to-l from-white/80 to-transparent active:from-white"
                        onClick={() => scrollToSiblingCard(1)}
                        aria-label={t("a11y.next")}
                    >
                        <ChevronRight className="w-12 h-12 text-gray-700" strokeWidth={3} />
                    </button>

                    <div
                        ref={containerRef}
                        className="relative w-full no-scrollbar select-none"
                        style={{
                            overflowX: "auto",
                            WebkitOverflowScrolling: "touch",
                            touchAction: "pan-x",
                        }}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                        onTouchStart={() => pauseOnInteract && setPaused(true)}
                        onTouchEnd={() => pauseOnInteract && setPaused(false)}
                        onTouchCancel={() => pauseOnInteract && setPaused(false)}
                    >
                        <div
                            ref={trackRef}
                            className="flex will-change-transform"
                            style={{
                                gap: `${gapPx}px`,
                                width: "max-content",
                                transform: "translateZ(0)",
                            }}
                        >
                            {rendered.map((feature, idx) => {
                                const Icon = feature.icon as IconComponent | undefined;
                                return (
                                    <div
                                        key={idx}
                                        className="relative bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow"
                                        style={{ minWidth: `${cardMinWidth}px` }}
                                    >
                                        <div className="mb-6">
                                            {Icon ? <Icon className="w-12 h-12 text-blue-600" /> : null}
                                            <div className="text-xl font-semibold mt-4">{feature.title}</div>
                                        </div>
                                        <p className="text-gray-600">{feature.description}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
